/** 宿泊管理のタスク管理タブを表す URL の tabType。宿泊管理画面のタブの値と、タスク一覧が書き出す URL で共有する */
export const RESERVATION_TASK_TAB_TYPE = 'reservationTask' as const

/** 一覧 API の1ページの件数。backend が固定で返す件数と同じ */
export const RESERVATION_TASK_PAGE_SIZE = 50

/** 予約ステータス。値は backend の ReservationTaskReservationStatus と同じ */
export const ReservationTaskReservationStatus = {
  New: 1,
  Updated: 2,
  Canceled: 3,
} as const
export type ReservationTaskReservationStatus = (typeof ReservationTaskReservationStatus)[keyof typeof ReservationTaskReservationStatus]

/** 対応ステータス。値は backend の ReservationTaskHandlingStatus と同じ */
export const ReservationTaskHandlingStatus = {
  Unhandled: 1,
  InProgress: 2,
  Completed: 3,
} as const
export type ReservationTaskHandlingStatus = (typeof ReservationTaskHandlingStatus)[keyof typeof ReservationTaskHandlingStatus]

/** 選択肢の並び順（メニュー・絞り込み・URL の検証で共通に使う） */
export const RESERVATION_TASK_RESERVATION_STATUSES: ReservationTaskReservationStatus[] = Object.values(ReservationTaskReservationStatus)
export const RESERVATION_TASK_HANDLING_STATUSES: ReservationTaskHandlingStatus[] = Object.values(ReservationTaskHandlingStatus)

/**
 * 並び替えの項目。値は一覧 API の sortKey の値。
 * 部屋タイプ / 室数は、backend がパフォーマンス上の理由で並び替えを廃止したため含めない
 */
export const ReservationTaskSortKey = {
  ReservationStatus: 'reservationStatus',
  ReservationUpdatedAt: 'reservationUpdatedAt',
  CheckinDate: 'checkinDate',
  GuestName: 'guestName',
  HandlingStatus: 'handlingStatus',
} as const
export type ReservationTaskSortKey = (typeof ReservationTaskSortKey)[keyof typeof ReservationTaskSortKey]

type ReservationTaskSortOrder = 'asc' | 'desc'

/** 並び替えの見出し（SortableTableHeader）と同じ { key, order } の形。URL にもこの形で書く */
export type ReservationTaskSort = {
  key: ReservationTaskSortKey
  order: ReservationTaskSortOrder
}

type ReservationTaskRoom = {
  roomTypeName: string
  roomCount: number
}

export type ReservationTaskStaff = {
  id: number
  name: string
}

/** 変わった項目1つ。difference は差額など、無ければ空文字 */
type ReservationTaskChangedItem = {
  label: string
  before: string
  after: string
  difference: string
}

/** 宿泊日ごとの変更1つ。stayDate は 'YYYY-MM-DD' */
type ReservationTaskDailyChange = {
  stayDate: string
  before: string
  after: string
}

export type ReservationTaskChangeSummary = {
  summary: string
  changes: ReservationTaskChangedItem[]
  dailyChanges: ReservationTaskDailyChange[]
}

/** 一覧 API の tasks[] の1件 */
export type ReservationTask = {
  id: number
  reservationUuid: string
  reservationId: string
  siteController: string
  reservationStatus: ReservationTaskReservationStatus
  checkinDate: string | null
  nights: number
  rooms: ReservationTaskRoom[]
  guestName: string | null
  /** 設計書 4.1 で一覧 API に追加される。宿泊者名と同じ人のカナで、空なら null */
  guestNameKana: string | null
  handlingStatus: ReservationTaskHandlingStatus
  staff: ReservationTaskStaff | null
  reservationUpdatedAt: string
  changeSummary: ReservationTaskChangeSummary | null
}

/** 絞り込み条件。名前と値の形は一覧 API のパラメーターと同じ。未指定は空配列・空文字 */
export type ReservationTaskFilter = {
  reservationStatuses: ReservationTaskReservationStatus[]
  handlingStatuses: ReservationTaskHandlingStatus[]
  staffIds: number[]
  /** 'YYYY-MM-DD' または 'YYYY-MM-DD HH:mm'（設計書 4.2） */
  reservationUpdatedAtFrom: string
  reservationUpdatedAtTo: string
  /** 'YYYY-MM-DD' */
  checkinDateFrom: string
  checkinDateTo: string
}

export type ReservationTaskQuery = {
  filter: ReservationTaskFilter
  sort: ReservationTaskSort
  page: number
}

export type ReservationTaskListParams = Partial<ReservationTaskFilter> & {
  sortKey: ReservationTaskSortKey
  sortOrder: ReservationTaskSortOrder
  page: number
}

export type ReservationTaskListResponse = {
  total: number
  hasMore: boolean
  tasks: ReservationTask[]
}

export type ReservationTaskUnhandledTotalResponse = {
  unhandledTotal: number
}

/** 更新 API（設計書 4.3）。送った項目だけが更新される。staffId の null は割り当てを外す */
export type ReservationTaskUpdateParams = {
  taskIds: number[]
  handlingStatus?: ReservationTaskHandlingStatus
  staffId?: number | null
}

/** 1件・まとめての更新で、対象のタスク以外に送る内容 */
export type ReservationTaskChange = Omit<ReservationTaskUpdateParams, 'taskIds'>

/** 表示名は言語の切り替えに追従するよう、キーだけを持ち描画のたびに t() で引く */
export const reservationTaskReservationStatusLabelKeys: Record<ReservationTaskReservationStatus, string> = {
  [ReservationTaskReservationStatus.New]: 'reservationTask.ReservationStatusNew',
  [ReservationTaskReservationStatus.Updated]: 'reservationTask.ReservationStatusUpdated',
  [ReservationTaskReservationStatus.Canceled]: 'reservationTask.ReservationStatusCanceled',
}

export const reservationTaskHandlingStatusLabelKeys: Record<ReservationTaskHandlingStatus, string> = {
  [ReservationTaskHandlingStatus.Unhandled]: 'reservationTask.HandlingStatusUnhandled',
  [ReservationTaskHandlingStatus.InProgress]: 'reservationTask.HandlingStatusInProgress',
  [ReservationTaskHandlingStatus.Completed]: 'reservationTask.HandlingStatusCompleted',
}
